// src/pages/Dashboard.jsx
import React, { useState, useMemo, useRef, memo, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { LayoutDashboard } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { apiFetch, ensureAuthOrRedirect,  redirectToLogin, attachExpiryTimer, getToken } from "../utils/auth";
import { MODAL_CONFIG } from "../utils/errors";
import FrostedModal from "../components/modals/FrostedModal";
/* ===========================
   Helpers (module scope)
   =========================== */

const DASHBOARD_API = "https://whiteintel.io/api/regular/app/v2/dashboard/dashboard_stats.php";
const WATCHLISTS_API = "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_domains.php";


// Compact, enterprise-y Stat Card
function StatCard({ title, info, value, trend, loading, viewAll, onViewAll }) {
  return (
    <div className="border rounded-sm border-sky-700/60 shadow-[0_0_12px_rgba(56,189,248,0.06)] backdrop-blur-sm bg-[#06101e]/60 p-5 flex flex-col relative h-[240px]">
      <div>
        <h3 className="text-base font-bold text-neutral-300">{title}</h3>
        {info && <p className="mt-1 text-xs text-neutral-500 leading-snug">{info}</p>}
      </div>

      <div className="w-full border-b border-neutral-800 mt-2" />

      <div className="mt-4 text-3xl sm:text-4xl md:text-5xl font-light text-white tracking-tight">
        {loading ? <div className="h-8 w-24 bg-neutral-800/80 rounded animate-pulse" /> : value}
      </div>

      {trend && (
        <div className="mt-1 flex items-center text-sm">
          <span className={`${trend.color} flex items-center`}>{trend.icon} {trend.label}</span>
        </div>
      )}

      {viewAll && (
        <button
          onClick={onViewAll}
          className="absolute bottom-3 right-4 text-xs text-sky-400 hover:underline"
        >
          View All →
        </button>
      )}
    </div>
  );
}

function getJwtToken() {
  try {
    return (
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN ||
      ""
    );
  } catch {
    return "";
  }
}

function combineTimeline(raw) {
  const byMonth = new Map();
  for (const { Year, Month, Count } of raw || []) {
    const key = `${Year}-${String(Month).padStart(2, "0")}`;
    byMonth.set(key, (byMonth.get(key) || 0) + Number(Count || 0));
  }
  return Array.from(byMonth.entries())
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function combineTwoTimelines(tlA = [], tlB = []) {
  const map = new Map();
  const bump = (d, v) => map.set(d, (map.get(d) || 0) + (Number(v) || 0));
  for (const { date, count } of tlA) bump(date, count);
  for (const { date, count } of tlB) bump(date, count);
  return Array.from(map.entries())
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

// Normalizers
const pickNumber = (obj) => {
  if (obj == null) return 0;
  if (typeof obj === "number") return obj;
  if (typeof obj === "string" && !isNaN(Number(obj))) return Number(obj);
  if (Array.isArray(obj)) return obj.length;

  if (typeof obj === "object") {
    if (typeof obj.count === "number") return obj.count;
    if (typeof obj.value === "number") return obj.value;
    if (obj.data && typeof obj.data.count === "number") return obj.data.count;
    if (obj.total && typeof obj.total === "number") return obj.total;
    if (Array.isArray(obj.items)) return obj.items.length;
    if (Array.isArray(obj.data)) return obj.data.length;
    const firstNumeric = Object.values(obj).find((v) => typeof v === "number");
    if (typeof firstNumeric === "number") return firstNumeric;
  }
  return 0;
};

const pickArray = (obj) => {
  if (Array.isArray(obj)) return obj;
  if (obj && Array.isArray(obj.items)) return obj.items;
  if (obj && Array.isArray(obj.data)) return obj.data;
  if (obj && Array.isArray(obj.timeline)) return obj.timeline;
  return [];
};

// Safe POST wrapper


// “This month” trend (▲ red only if >0, else ↔ stable)
// “This month” trend:
// - If there are new records in the current month → ▲ +X since {Mon YYYY}
// - Otherwise → ↔ Stable since {last active Mon YYYY} (the most recent month with count > 0)
// - If there's never been activity → ↔ No activity observed
function computeThisMonthNewRecords(timeline = [], { now = new Date() } = {}) {
  if (!Array.isArray(timeline) || timeline.length === 0) {
    return { current: 0, trendUi: stableUi("No activity observed") };
  }

  // Normalize + sort
  const data = [...timeline]
    .map(d => ({ date: String(d.date), count: Number(d.count) || 0 }))
    .sort((a, b) => a.date.localeCompare(b.date));

  // Build quick lookup
  const map = new Map(data.map(d => [d.date, d.count]));

  // Current month key
  const currKey = ymKey(now.getFullYear(), now.getMonth() + 1);
  const current = map.get(currKey) || 0;

  if (current > 0) {
    // New activity this month
    return {
      current,
      trendUi: upUi(`+${current.toLocaleString()} since ${monthYear(now)}`),
    };
  }

  // Find the most recent month with count > 0
  let lastActiveKey = null;
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i].count > 0) {
      lastActiveKey = data[i].date;
      break;
    }
  }

  if (!lastActiveKey) {
    // Never had activity at all
    return { current: 0, trendUi: stableUi("No activity observed") };
  }

  const { y, m } = keyToParts(lastActiveKey);
  const lastActiveLabel = monthYear(new Date(y, m - 1, 1));

  return {
    current: 0,
    trendUi: stableUi(`Stable since ${lastActiveLabel}`),
  };

  // helpers



  function ymKey(y, m) { return `${y}-${String(m).padStart(2, "0")}`; }
  function keyToParts(k) { const [yy, mm] = k.split("-").map(Number); return { y: yy, m: mm }; }
  function monthYear(date) { return date.toLocaleString("en-US", { month: "short", year: "numeric" }); }
  function upUi(text)     { return { color: "text-red-500",     icon: "▲", label: (<>{text}</>) }; }
  function stableUi(text) { return { color: "text-neutral-400", icon: "↔", label: (<>{text}</>) }; }
}



function typeDotClass(t) {
  const k = String(t || "").toLowerCase();
  if (k === "stealer") return "bg-red-500";
  if (k === "combolist") return "bg-orange-400";
  return "bg-sky-400";
}
const formatNumber = (n) => (n ?? 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

function TableSkeletonRows({ rows = 8 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={`s-${i}`} className="border-b border-neutral-800">
          <td className="py-2 px-3"><div className="h-4 w-56 bg-neutral-800/80 rounded animate-pulse" /></td>
          <td className="py-2 px-3">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-neutral-800/80 animate-pulse" />
              <div className="h-4 w-20 bg-neutral-800/80 rounded animate-pulse" />
            </div>
          </td>
          <td className="py-2 px-3"><div className="h-4 w-24 bg-neutral-800/80 rounded animate-pulse" /></td>
          <td className="py-2 px-3 text-right"><div className="ml-auto h-4 w-12 bg-neutral-800/80 rounded animate-pulse" /></td>
        </tr>
      ))}
    </>
  );
}

// Stable tooltip element
const DarkTip = memo(function DarkTipInner({ active, payload, label }) {
  return active && payload?.length ? (
    <div className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs text-neutral-200 shadow-xl">
      <div className="mb-1 text-neutral-400">{label}</div>
      <div className="font-semibold text-white">{payload[0].value}</div>
    </div>
  ) : null;
});

// Memoized AreaCard; re-renders only if props change
const AreaCard = memo(function AreaCard({ title, data, color = "#3b82f6" }) {
    const hasData = Array.isArray(data) && data.length > 0;
  return (
    

    <div className="border border-sky-700/60 shadow-[0_0_8px_rgba(56,189,248,0.15)] backdrop-blur-sm rounded-sm p-5 bg-[#06101e]/60">
      <h3 className="text-sm font-semibold text-neutral-300 mb-3">{title}</h3>
      <div className="h-64 relative">

        {!hasData ? (
          <div className="flex items-center justify-center h-full text-neutral-400 text-sm">
            <span>No data available for specified query.</span>
          </div>
        ) : (

        <ResponsiveContainer width="100%" height="100%" debounce={200}>
          <AreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
            <XAxis dataKey="date" stroke="#94a3b8" tickLine={false} axisLine={{ stroke: "#334155" }} />
            <YAxis stroke="#94a3b8" tickLine={false} axisLine={{ stroke: "#334155" }} />
            <Tooltip content={<DarkTip />} />
            <Area
              type="linear"
              dataKey="count"
              stroke={color}
              strokeWidth={2}
              fill={color}
              fillOpacity={0.15}
              dot={{ r: 3, stroke: "#fff", strokeWidth: 1, fill: color }}
              activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
        )}
      </div>
       
    </div>
  );
});

/* ===========================
   Component
   =========================== */

export default function Dashboard() {
    
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState("overview");
  const [selectedWatchlist, setSelectedWatchlist] = useState(null);

  const [dashError, setDashError] = useState("");

  const navigate = useNavigate();

function onViewAll(key) {
    let params = new URLSearchParams();

    if (key === "consumer") {
      params.set("type", "consumerRecords");
    } else if (key === "corporate") {
      params.set("type", "corporateRecords");
    }

    if (navigateQuery) {
      params.set("query", navigateQuery);
    }

    navigate(`/search?${params.toString()}`);
}

  // loading flags per metric (so each card/table updates independently)
  const [loading, setLoading] = useState({
    consumerCount: false,
    corporateCount: false,
    ipCount: false,
    compCount: false,
    hostnameCount: false,
    consTL: false,
    corpTL: false,
    ipTL: false,
    hostTL: false,
    latestCE: false,
    latestCoE: false,
  });
  const setL = (k, v) => setLoading(prev => ({ ...prev, [k]: v }));

  // Big numbers
  const [consumerCount, setConsumerCount] = useState(0);
  const [corporateCount, setCorporateCount] = useState(0);
  const [ipAddressCount, setIpAddressCount] = useState(0);
  const [computerCount, setComputerCount] = useState(0);
  const [hostnameCount, setHostnameCount] = useState(0);
  const [modal, setModal] = useState({ open: false, type: null });
    const openModalFor = (type) => setModal({ open: true, type });
    const closeModal = () => setModal({ open: false, type: null });
    const cfg = modal.type ? MODAL_CONFIG[modal.type] : null;
  // Timelines (state used for counts/trends), but charts will use FROZEN refs
  const [consumerIncidentTimeline, setConsumerIncidentTimeline] = useState([]);
  const [corporateIncidentTimeline, setCorporateIncidentTimeline] = useState([]);
  const [corporateIPTimeline, setCorporateIPTimeline] = useState([]);
  const [corporateHostTimeline, setCorporateHostTimeline] = useState([]);
  const [consRaw, setConsRaw] = useState([]);
  const [corpRaw, setCorpRaw] = useState([]);
  const [wlLoading, setWlLoading] = useState(true);
const [wlError, setWlError] = useState("");
const [watchlistQueries, setWatchlistQueries] = useState([]); // [{id,label}]

  // Frozen chart data (filled once per search)
  const corpChartRef = useRef([]);
  const consChartRef = useRef([]);
  const [chartTick, setChartTick] = useState(0); // force single repaint when we set frozen data

  // Events
  const [latestConsumerEvents, setLatestConsumerEvents] = useState([]);
  const [latestCorporateEvents, setLatestCorporateEvents] = useState([]);

  // Tabs / search
  const [activeTab, setActiveTab] = useState("consumer");
  const [query, setQuery] = useState("");
  const [wlOpen, setWlOpen] = useState(false);
  const [hasSearchQuery, setHasSearchQuery] = useState(null);
  

  // bump to identify "new search"; charts freeze per version
  const searchVersionRef = useRef(0);

  async function postMetric(metric, query, extra = {}) {
  const jwt = getJwtToken();
  const resp = await fetch(DASHBOARD_API, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    body: JSON.stringify({ query, metric, ...extra }),
  });

         if ([401, 419, 440].includes(resp.status)) {
      redirectToLogin();
      // throw to stop callers from continuing
      throw new Error(`AUTH_REDIRECT_${resp.status}`);
    }

    if (resp.status === 400) {
          const text = await resp.text();
          if (text.includes("Invalid email format")) {
            openModalFor("INVALID_EMAIL");
          }
          else if(text.includes("search limit"))
          {
            openModalFor("SEARCH_LIMIT");
          }
          else {
            openModalFor("GENERIC_ERROR");
          }
        
          return;
        }

  if (!resp.ok) {
    const errText = await safeText(resp);
    throw new Error(`dashboard_stats error ${resp.status}${errText ? `: ${errText.slice(0,120)}` : ""}`);
  }

  const bodyText = await safeText(resp);
  if (!bodyText) return defaultForMetric(metric);

  try {
    return JSON.parse(bodyText);
  } catch {
    return defaultForMetric(metric);
  }

  async function safeText(r) {
    try { return await r.text(); } catch { return ""; }
  }
  function defaultForMetric(m) {
    const zero = 0, empty = [];
    switch (m) {
      case "consumer_count":
      case "corporate_count":
      case "ip_address_count":
      case "computer_count":
      case "hostname_count":
        return zero;
      case "consumer_incident_timeline":
      case "corporate_incident_timeline":
      case "corporate_ip_timeline":
      case "corporate_hostname_timeline":
      case "latest_consumer_events":
      case "latest_corporate_events":
        return empty;
      default:
        return empty;
    }
  }
}

useEffect(() => {
  let aborted = false;

  async function loadWatchlistDomains() {
    setWlLoading(true);
    setWlError("");

    try {
      const jwtToken = getJwtToken();

      const resp = await fetch(WATCHLISTS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}),
        },
        body: JSON.stringify({ metric: "watchlist_domains" }),
      });

             if ([401, 419, 440].includes(resp.status)) {
      redirectToLogin();
      // throw to stop callers from continuing
      throw new Error(`AUTH_REDIRECT_${resp.status}`);
    }

      if (!resp.ok) throw new Error(`Request failed: ${resp.status}`);

      const data = await resp.json();

      // Your shape: [{ entry: "cimri.com" }, { entry: "test.com" }, ...]
      const domains = Array.isArray(data)
        ? data.map((x) => (x && typeof x.entry === "string" ? x.entry.trim() : null)).filter(Boolean)
        : Array.isArray(data?.data)
          ? data.data.map((x) => (x && typeof x.entry === "string" ? x.entry.trim() : null)).filter(Boolean)
          : [];

      // de-dupe + sort
      const unique = [...new Set(domains)].sort((a, b) => a.localeCompare(b));

      if (!aborted) {
        setWatchlistQueries(unique.map((d) => ({ id: d, label: d })));
      }
    } catch (e) {
      if (!aborted) setWlError(e?.message || "Unable to load watchlist domains");
    } finally {
      if (!aborted) setWlLoading(false);
    }
  }

  loadWatchlistDomains();
  return () => { aborted = true; };
}, []);


  /* ===== Metric-specific wrappers ===== */
  async function fetchConsumerCount(q) {
  const res = await postMetric("consumer_count", q);
  return (
    Number(res?.stealer_consumer_count || 0) +
    Number(res?.combolist_consumer_count || 0)
  );
}
  async function fetchCorporateCount(q) {
  const res = await postMetric("corporate_count", q);
  return (
    Number(res?.stealer_corporate_count || 0) +
    Number(res?.combolist_corporate_count || 0)
  );
}
  async function fetchIpAddressCount(q) { return pickNumber(await postMetric("ip_address_count", q)); }
  async function fetchComputerCount(q) { return pickNumber(await postMetric("computer_count", q)); }
  async function fetchHostnameCount(q) { return pickNumber(await postMetric("hostname_count", q)); }
  async function fetchConsumerIncidentTimeline(q) { return pickArray(await postMetric("consumer_incident_timeline", q)); }
  async function fetchCorporateIncidentTimeline(q) { return pickArray(await postMetric("corporate_incident_timeline", q)); }
  async function fetchLatestConsumerEvents(q) { const d = await postMetric("latest_consumer_events", q); return Array.isArray(d) ? d : (d.items || d.data || []); }
  async function fetchLatestCorporateEvents(q) { const d = await postMetric("latest_corporate_events", q); return Array.isArray(d) ? d : (d.items || d.data || []); }
  async function fetchCorporateIPTimeline(q) { return pickArray(await postMetric("corporate_ip_timeline", q)); }
  async function fetchCorporateHostnameTimeline(q) { return pickArray(await postMetric("corporate_hostname_timeline", q)); }

  const anyLoading = Object.values(loading).some(Boolean);
  const [navigateQuery, setNavigateQuery] = useState("");
  // Accept optional overrideQuery (e.g., from Watchlist)
// Replace your handleSearch with this safe version
const handleSearch = async (arg) => {
  // If arg is a React event (click/keyboard), ignore it as override
  const isEventLike =
    arg &&
    typeof arg === "object" &&
    ("preventDefault" in arg || "nativeEvent" in arg || "type" in arg);

  const overrideQuery = !isEventLike && typeof arg === "string" ? arg : undefined;

  const raw = overrideQuery ?? query ?? "";
  const q = String(raw).trim();
  if (!q) return;

  // keep input + state in sync when coming from Watchlist
  if (overrideQuery && overrideQuery !== query) {
    setQuery(""); // keep your previous behavior
    setSelectedWatchlist(overrideQuery);
    setNavigateQuery(overrideQuery);
  } else {
    setNavigateQuery(q);
    setSelectedWatchlist(null);
  }

  setHasSearchQuery(true);
  setDashError("");

  // --- rest of your existing handleSearch body unchanged below ---
  searchVersionRef.current += 1;
  corpChartRef.current = [];
  consChartRef.current = [];
  setChartTick((t) => t + 1);

  setL("consumerCount", true);
  fetchConsumerCount(q)
    .then(setConsumerCount)
    .catch((err) => setDashError(err?.message || "consumer_count error"))
    .finally(() => setL("consumerCount", false));

  setL("corporateCount", true);
  fetchCorporateCount(q)
    .then(setCorporateCount)
    .catch((err) => setDashError(err?.message || "corporate_count error"))
    .finally(() => setL("corporateCount", false));

  setL("ipCount", true);
  fetchIpAddressCount(q)
    .then(setIpAddressCount)
    .catch((err) => setDashError(err?.message || "ip_address_count error"))
    .finally(() => setL("ipCount", false));

  setL("compCount", true);
  fetchComputerCount(q)
    .then(setComputerCount)
    .catch((err) => setDashError(err?.message || "computer_count error"))
    .finally(() => setL("compCount", false));

  setL("hostnameCount", true);
  fetchHostnameCount(q)
    .then(setHostnameCount)
    .catch((err) => setDashError(err?.message || "hostname_count error"))
    .finally(() => setL("hostnameCount", false));

  setL("consTL", true);
  fetchConsumerIncidentTimeline(q)
    .then((raw) => {
      const tl = combineTimeline(raw);
      setConsumerIncidentTimeline(tl);
      setConsRaw(raw);
      if (consChartRef.current.length === 0 && tl.length) {
        consChartRef.current = tl;
        setChartTick((t) => t + 1);
      }
    })
    .catch((err) => setDashError(err?.message || "consumer_timeline error"))
    .finally(() => setL("consTL", false));

  setL("corpTL", true);
  fetchCorporateIncidentTimeline(q)
    .then((raw) => {
      const tl = combineTimeline(raw);
      setCorporateIncidentTimeline(tl);
      setCorpRaw(raw);
      if (corpChartRef.current.length === 0 && tl.length) {
        corpChartRef.current = tl;
        setChartTick((t) => t + 1);
      }
    })
    .catch((err) => setDashError(err?.message || "corporate_timeline error"))
    .finally(() => setL("corpTL", false));

  setL("ipTL", true);
  fetchCorporateIPTimeline(q)
    .then((raw) => setCorporateIPTimeline(combineTimeline(raw)))
    .catch((err) => setDashError(err?.message || "ip_timeline error"))
    .finally(() => setL("ipTL", false));

  setL("hostTL", true);
  fetchCorporateHostnameTimeline(q)
    .then((raw) => setCorporateHostTimeline(combineTimeline(raw)))
    .catch((err) => setDashError(err?.message || "hostname_timeline error"))
    .finally(() => setL("hostTL", false));

  setL("latestCE", true);
  fetchLatestConsumerEvents(q)
    .then(setLatestConsumerEvents)
    .catch((err) => setDashError(err?.message || "latest_consumer_events error"))
    .finally(() => setL("latestCE", false));

  setL("latestCoE", true);
  fetchLatestCorporateEvents(q)
    .then(setLatestCorporateEvents)
    .catch((err) => setDashError(err?.message || "latest_corporate_events error"))
    .finally(() => setL("latestCoE", false));
};



  // Trends (based on live timelines; charts are frozen separately)
  const totalTimeline = useMemo(
    () => combineTwoTimelines(consumerIncidentTimeline, corporateIncidentTimeline),
    [consumerIncidentTimeline, corporateIncidentTimeline]
  );
  const totalTrend = computeThisMonthNewRecords(totalTimeline);
  const consumerTrend = computeThisMonthNewRecords(consumerIncidentTimeline);
  const corporateTrend = computeThisMonthNewRecords(corporateIncidentTimeline);
  const ipTrend = computeThisMonthNewRecords(corporateIPTimeline);
  const hostTrend = computeThisMonthNewRecords(corporateHostTimeline);

  // Cards
  const cardData = [
    {
      key: "total",
      title: "Total Leaks",
      info: "Combined consumer and corporate leaks detected.",
      value: formatNumber((consumerCount || 0) + (corporateCount || 0)),
      trend: totalTrend.trendUi,
      loading: loading.consumerCount || loading.corporateCount,
      viewAll: false,
    },
    {
      key: "consumer",
      title: "Consumer Leaks",
      info: "Total exposed consumer credentials for queried asset.",
      value: formatNumber(consumerCount || 0),
      trend: consumerTrend.trendUi,
      loading: loading.consumerCount,
      viewAll: true,
    },
    {
      key: "corporate",
      title: "Corporate Leaks",
      info: "Total exposed corporate credentials for queried asset.",
      value: formatNumber(corporateCount || 0),
      trend: corporateTrend.trendUi,
      loading: loading.corporateCount,
      viewAll: true,
    },
    {
      key: "ip",
      title: "Infected IP Addresses",
      info: "Unique IPs linked to corporate email addresses.",
      value: formatNumber(ipAddressCount || 0),
      trend: ipTrend.trendUi,
      loading: loading.ipCount || loading.ipTL,
      viewAll: false,
    },
    {
      key: "computers",
      title: "Affected Computers",
      info: "Unique devices linked to corporate email addresses.",
      value: formatNumber(computerCount || 0),
      trend: hostTrend.trendUi, // if you prefer no trend, set to null
      loading: loading.compCount,
      viewAll: false,
    }

  ];

  


function ymKey(y, m) { return `${y}-${String(m).padStart(2, "0")}`; }
function nowYM(d = new Date()) { return { y: d.getFullYear(), m: d.getMonth() + 1 }; }
function prevYM(y, m) { m -= 1; if (m === 0) { m = 12; y -= 1; } return { y, m }; }

// Build map: { "YYYY-MM": { stealer: n, combolist: n } } from raw timeline
// raw = [{Year, Month, Count, source}, ...]
function buildMonthlySourceTotals(raw = []) {
  const totals = new Map();
  for (const row of raw) {
    const y = Number(row?.Year), m = Number(row?.Month);
    if (!y || !m) continue;
    const k = ymKey(y, m);
    const src = String(row?.source || '').toLowerCase();
    const cnt = Number(row?.Count) || 0;
    if (!['stealer','combolist'].includes(src)) continue;

    if (!totals.has(k)) totals.set(k, { stealer: 0, combolist: 0 });
    const bucket = totals.get(k);
    bucket[src] += cnt;
  }
  return totals; // Map<YYYY-MM, {stealer, combolist}>
}

function combineMonthlyMaps(mapA, mapB) {
  const out = new Map();
  const add = (k, obj) => {
    if (!out.has(k)) out.set(k, { stealer: 0, combolist: 0 });
    out.get(k).stealer   += obj.stealer   || 0;
    out.get(k).combolist += obj.combolist || 0;
  };

  for (const [k, v] of (mapA || [])) add(k, v);
  for (const [k, v] of (mapB || [])) add(k, v);
  return out;
}

function getThisAndLastMonthTotals(monthMap, now = new Date()) {
  const { y, m } = nowYM(now);
  const thisMonth = getMonthTotals(monthMap, y, m);
  const p = prevYM(y, m);
  const lastMonth = getMonthTotals(monthMap, p.y, p.m);
  return { thisMonth, lastMonth };
}

function getMonthTotals(monthMap, y, m) {
  const k = ymKey(y, m);
  const v = monthMap.get(k);
  return v ? { ...v } : { stealer: 0, combolist: 0 };
}



// 1) Build per-source monthly totals for each
const consMap = buildMonthlySourceTotals(consRaw);
const corpMap = buildMonthlySourceTotals(corpRaw);
// 2) Combine (consumer + corporate)
const combinedMap = combineMonthlyMaps(consMap, corpMap);

// 3) Pull this month + last month (for each source)
const { thisMonth, lastMonth } = getThisAndLastMonthTotals(combinedMap);

// Now you have:
const thisMonthCombolist = thisMonth.combolist; // e.g., 333 from your sample
const thisMonthStealer   = thisMonth.stealer;   // e.g., 61


  function RecentExposureCard({ combolist = 0, stealer = 0, loading = false }) {
  return (
    <div className="border rounded-sm border-sky-700/60 shadow-[0_0_12px_rgba(56,189,248,0.06)] backdrop-blur-sm bg-[#06101e]/60 p-5 flex flex-col">
      <div>
        <h3 className="text-base font-bold text-neutral-300">Recent Exposure</h3>
        <p className="mt-1 text-xs text-neutral-500 leading-snug">
          This month’s combined new records by source.
        </p>
      </div>

      <div className="w-full border-b border-neutral-800 mt-2" />

      <div className="mt-4 grid grid-cols-2 gap-4">
        {/* Combolists */}
        <div className="rounded-sm border border-neutral-800 bg-[#0a1224]/60 p-4">
          <div className="flex items-center gap-2 text-sm text-neutral-300">
            <span className="inline-block w-2 h-2 rounded-full bg-orange-400" />
            Combolists
          </div>
          <div className="mt-2 text-3xl font-light text-white">
            {loading ? <div className="h-8 w-24 bg-neutral-800/80 rounded animate-pulse" /> : combolist.toLocaleString()}
          </div>
        </div>

        {/* Stealers */}
        <div className="rounded-sm border border-neutral-800 bg-[#0a1224]/60 p-4">
          <div className="flex items-center gap-2 text-sm text-neutral-300">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
            Stealers
          </div>
          <div className="mt-2 text-3xl font-light text-white">
            {loading ? <div className="h-8 w-24 bg-neutral-800/80 rounded animate-pulse" /> : stealer.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Optional tiny footer */}
      <div className="mt-auto pt-3 text-[11px] text-neutral-500">
        Updated from consumer & corporate event feeds
      </div>
    </div>
  );
}

  // Events card
  function EventsWideCard({ consumerEvents = [], corporateEvents = [], activeTab, setActiveTab, loading }) {
    const eventsAreLoading = loading.latestCE || loading.latestCoE;
    const rows = activeTab === "consumer" ? consumerEvents : corporateEvents;

    return (
      <div className="sm:col-span-2 border rounded-sm border-sky-700/60 shadow-[0_0_12px_rgba(56,189,248,0.06)] backdrop-blur-sm bg-[#06101e]/60 p-5 flex flex-col h-[240px]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-neutral-300">Latest Events</h3>
          <div className="inline-flex items-center gap-2 rounded-lg border border-sky-900/30 bg-[#0a1224]/70 p-1">
            <button
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${activeTab === "consumer" ? "bg-sky-500/20 text-sky-200" : "text-neutral-300 hover:bg-white/5"}`}
              onClick={() => setActiveTab("consumer")}
            >
              Consumer
            </button>
            <button
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${activeTab === "corporate" ? "bg-sky-500/20 text-sky-200" : "text-neutral-300 hover:bg-white/5"}`}
              onClick={() => setActiveTab("corporate")}
            >
              Corporate
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <table className="w-full text-sm text-neutral-300">
            <thead className="sticky top-0 z-10 bg-[#06101e]">
              <tr className="border-b border-neutral-700">
                <th className="text-left py-2 px-3 font-medium">Application</th>
                <th className="text-left py-2 px-3 font-medium">Type</th>
                <th className="text-left py-2 px-3 font-medium">Date</th>
                <th className="text-right py-2 px-3 font-medium">Records</th>
              </tr>
            </thead>
          <tbody>
  {eventsAreLoading ? (
    <TableSkeletonRows rows={8} />
  ) : rows.length === 0 ? (
    <tr>
      <td
        colSpan={4}
        className="py-6 px-3 text-center text-neutral-400"
      >
        No events found for this category.
      </td>
    </tr>
  ) : (
    rows.map((ev, idx) => (
      <tr
        key={idx}
        className="border-b border-neutral-800 hover:bg-neutral-800/50"
      >
        <td className="py-2 px-3 truncate">{ev.application}</td>
        <td className="py-2 px-3 capitalize">
          <span className="inline-flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${typeDotClass(ev.type)}`} />
            {ev.type}
          </span>
        </td>
        <td className="py-2 px-3">
          {ev.date?.slice(0, 10) || ev.date}
        </td>
        <td className="py-2 px-3 text-right text-white">
          +{(ev.records ?? 0).toLocaleString()}
        </td>
      </tr>
    ))
  )}
</tbody>

          </table>
        </div>
      </div>
    );
  }

  // Enterprise loading banner (compact)
  function LoadingBanner() {
    const chips = [
      ["Consumers", loading.consumerCount || loading.consTL],
      ["Corporate", loading.corporateCount || loading.corpTL],
      ["IPs", loading.ipCount || loading.ipTL],
      ["Hosts", loading.hostnameCount || loading.hostTL],
      ["Computers", loading.compCount],
      ["Events", loading.latestCE || loading.latestCoE],
    ].filter(([, is]) => is);

    if (chips.length === 0) return null;

    return (
      <div className="mx-6 mb-2 rounded-sm border border-sky-900/30 bg-[#0a1224]/70 px-3 py-2 flex items-center gap-3 text-sm text-neutral-300">
        <span className="inline-flex h-3 w-3 animate-spin rounded-full border-2 border-sky-400 border-t-transparent" />
        <span className="font-medium text-sky-200">Fetching live intelligence…</span>
        <div className="flex flex-wrap gap-2">
          {chips.map(([label], i) => (
            <span
              key={i}
              className="px-2 py-0.5 rounded border border-sky-900/40 bg-[#06101e]/80 text-xs text-sky-300"
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      <header
        className="bg-transparent px-6 py-4 transition-all duration-300"
        style={{ marginLeft: collapsed ? "72px" : "240px" }}
      >
     <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-[24px] font-bold leading-tight">Dashboard</h1>
           
              </div>
            </div>
      </header>


      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey={active}
          onChange={setActive}
        />

        <main className="flex-1 flex flex-col w-full">
          {/* Toolbar */}
       <div className="px-4 md:px-6 py-4">
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
    
    {/* Watchlist dropdown */}
  <div
  className="relative w-56"
  tabIndex={0}
  onBlur={() => setTimeout(() => setWlOpen(false), 100)}
>
  <button
    type="button"
    onClick={() => {
      if (!wlLoading) setWlOpen((v) => !v);
    }}
    disabled={wlLoading}
    className={`
      inline-flex w-full items-center justify-between rounded-lg
      border ${wlError ? "border-red-800/50" : "border-sky-900/30"}
      ${wlLoading ? "bg-[#0a1224]/60" : "bg-[#0a1224]/80"}
      backdrop-blur-sm px-3 py-2.5 text-sm text-white
      shadow-[0_0_10px_rgba(56,189,248,0.08)]
      hover:bg-[#0a1224]/90 transition-colors disabled:opacity-60
    `}
  >
    <span className="truncate">
      {wlLoading
        ? "Loading…"
        : wlError
          ? "Error loading watchlist"
          : watchlistQueries.length === 0
            ? "No watchlist domains"
            : (selectedWatchlist
                ? (watchlistQueries.find((q) => q.id === selectedWatchlist)?.label || selectedWatchlist)
                : "Select a Domain")}
    </span>
    <svg className="h-4 w-4 text-sky-300/70" viewBox="0 0 20 20" fill="currentColor">
      <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
    </svg>
  </button>

  {wlOpen && !wlLoading && !wlError && watchlistQueries.length > 0 && (
    <div
      className="
        absolute z-20 mt-2 w-full overflow-hidden rounded-lg
        border border-sky-900/30 bg-[#0a1224]/90 backdrop-blur-sm
        shadow-[0_0_12px_rgba(56,189,248,0.08)]
      "
    >
      {watchlistQueries.map((opt) => (
        <button
          key={opt.id}
          onClick={() => {
            setSelectedWatchlist(opt.id); // <- domain string
            setWlOpen(false);
            handleSearch(opt.id); 
          }}
          className="block w-full px-3 py-2 text-left text-sm text-neutral-200 hover:bg-white/5"
        >
          {opt.label}
        </button>
      ))}
    </div>
  )}

  {/* Optional: retry on error */}
  {wlOpen && wlError && (
    <div className="absolute z-20 mt-2 w-full rounded-lg border border-red-800/50 bg-[#1a1212]/90 p-3 text-sm text-red-200">
      {wlError}
    </div>
  )}
</div>

    {/* Search input */}
    <div className="relative w-72">
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
        placeholder="e.g. acme.com"
        inputMode="search"
        className="w-full h-11 rounded-lg border border-sky-900/30 bg-[#0a1224]/80 backdrop-blur-sm pl-10 pr-3 text-base text-white placeholder-neutral-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40 shadow-[0_0_10px_rgba(56,189,248,0.06)]"
      />
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-sky-300" viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" d="M10 2a8 8 0 105.293 14.293l4.147 4.147a1 1 0 001.414-1.414l-4.147-4.147A8 8 0 0010 2zm-6 8a6 6 0 1112 0 6 6 0 01-12 0z" clipRule="evenodd" />
      </svg>
    </div>

    {/* Search button */}
    <button
      onClick={handleSearch}
      disabled={!hasSearchQuery}
      className="h-11 rounded-lg border border-sky-900/30 bg-sky-600/20 px-4 text-sm text-sky-200 hover:bg-sky-600/30 disabled:opacity-50"
    >
      Search
    </button>

    {dashError && <div className="text-xs text-red-400 ml-2">{dashError}</div>}
  </div>
</div>


          {/* Enterprise loading banner */}
          {anyLoading && <LoadingBanner />}

          {/* Cards + Charts */}
          <div className="relative isolate">
            {/* Cards */}
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {cardData.map((card) => (
                <StatCard
                  key={card.key}
                  title={card.title}
                  info={card.info}
                  value={card.value}
                  trend={card.trend}
                  loading={card.loading}
                  viewAll={card.viewAll}
                  onViewAll={() => onViewAll(card.key)}
                />
              ))}

              <RecentExposureCard
    combolist={thisMonthCombolist}
    stealer={thisMonthStealer}
    loading={loading.consTL || loading.corpTL}
  />

              <EventsWideCard
                consumerEvents={latestConsumerEvents}
                corporateEvents={latestCorporateEvents}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                loading={loading}
              />
            </div>

            {/* Charts (FROZEN datasets) */}
            <div className="px-6 pb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <AreaCard
                title="Corporate Incident Timeline"
                data={corpChartRef.current /* stable ref */}
                color="#facc15"
              />
              <AreaCard
                title="Consumer Incident Timeline"
                data={consChartRef.current /* stable ref */}
                color="#60a5fa"
              />
            </div>

            {/* Blur overlay when no search query */}
            {!hasSearchQuery && (
              <div className="absolute inset-0 z-50">
                <div className="absolute inset-0 backdrop-blur-sm bg-[#000414]/50" />
                <div className="relative z-10 flex items-center justify-center h-[600px] px-4">
                  <div className="max-w-xl w-full text-center rounded-2xl border border-sky-900/40 bg-[#0a1224]/90 backdrop-blur-md shadow-[0_0_24px_rgba(56,189,248,0.10)] px-6 py-8">
                    <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-sm bg-sky-500/10 border border-sky-700/40 shadow-inner">
                      <LayoutDashboard className="text-sky-400" />
                    </div>
                    <h2 className="text-white text-2xl font-semibold tracking-tight">
                      Welcome to <span className="text-sky-400">Dashboard</span>
                    </h2>
                    <p className="mt-2 text-neutral-300">
                      Start by entering a domain or pick a Watchlist query.
                      We’ll surface exposures, infected assets, and trends across our intelligence sources.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
         {cfg && (
                  <FrostedModal
                    open={modal.open}
                    onClose={closeModal}
                    icon={cfg.icon}
                    title={cfg.title}
                    description={cfg.description}
                    primaryAction={
                      cfg.primary && {
                        label: cfg.primary.label,
                        onClick: () => {
                          if (cfg.primary.href) window.location.href = cfg.primary.href;
                          else closeModal();
                        },
                      }
                    }
                    secondaryAction={
                      cfg.secondary && {
                        label: cfg.secondary.label,
                        onClick: closeModal,
                      }
                    }
                  />
                )}
    </div>
  );
}
